import React, { useEffect, useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogTitle, DialogBody } from '../dialog';
import {
  OT_RESULTADOS, OT_RESULTADO_LABELS, MOTIVO_MAX_LEN, validarCierre, folioTexto,
} from '../../utils/ordenesTrabajo';

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * Close or reopen an OT. Closing documents how it ended and why; nothing is
 * deleted. Reopening asks for a motivo that goes to the thread.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Object} props.ot
 * @param {'cerrar'|'reabrir'} props.modo
 * @param {Function} props.onConfirm  - cerrar: ({resultado, motivo}); reabrir: (motivo)
 */
export function OtCerrarDialog({ isOpen, onClose, ot, modo = 'cerrar', onConfirm }) {
  const [resultado, setResultado] = useState(OT_RESULTADOS.ENTREGADA);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setResultado(OT_RESULTADOS.ENTREGADA);
    setMotivo('');
    setError(null);
    setBusy(false);
  }, [isOpen]);

  const confirmar = async () => {
    if (modo === 'cerrar') {
      const msg = validarCierre({ resultado, motivo });
      if (msg) { setError(msg); return; }
    }
    setBusy(true);
    try {
      if (modo === 'cerrar') await onConfirm({ resultado, motivo: motivo.trim() });
      else await onConfirm(motivo.trim());
      onClose?.();
    } catch (e) {
      console.error('cierre OT failed', e);
      setError(e.message || 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen || !ot) return null;
  const esCierre = modo === 'cerrar';

  return (
    <Dialog open onClose={busy ? () => {} : onClose} size="sm">
      <DialogTitle>
        <span className="text-azul-surelia">{esCierre ? `Cerrar ${folioTexto(ot.folio)}` : `Reabrir ${folioTexto(ot.folio)}`}</span>
      </DialogTitle>
      <DialogBody>
        <div className="space-y-3">
          {esCierre && (
            <div className="grid grid-cols-3 gap-2">
              {Object.values(OT_RESULTADOS).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setResultado(r)}
                  className={`rounded-lg border px-2 py-2 text-sm transition-colors ${
                    resultado === r ? 'border-azul-surelia bg-azul-surelia text-white' : 'border-gray-300 text-gray-700 hover:border-azul-surelia/50'
                  }`}
                >
                  {OT_RESULTADO_LABELS[r]}
                </button>
              ))}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Motivo{esCierre && resultado === OT_RESULTADOS.ENTREGADA ? ' (opcional)' : ''}
            </label>
            <textarea
              rows={3}
              value={motivo}
              maxLength={MOTIVO_MAX_LEN}
              onChange={(e) => setMotivo(e.target.value)}
              className={field}
              autoFocus
            />
          </div>
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={onClose} disabled={busy}>Cancelar</Button>
            <Button color="surelia" onClick={confirmar} disabled={busy}>
              {busy ? 'Guardando…' : esCierre ? 'Cerrar orden' : 'Reabrir'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default OtCerrarDialog;
